import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import DemandCard, { type DraftFit, type GeneratorDraft, loadPlan, savePlan } from "../components/demand/DemandCard";
import { type PlanParams, fmtHHMM, fmtPct } from "../components/demand/demandMath";
import SimulationCard, { type TimetableSource } from "../components/simulation/SimulationCard";
import type {
  AutoAssignReport,
  DayPattern,
  DispatchMode,
  Driver,
  DriverDuty,
  GenerateScheduleParams,
  GenerationResult,
  HeadwayBand,
  ListResponse,
  Route,
  ScheduleAuditEvent,
  ScheduleDiff,
  ScheduleImport,
  ScheduleImportDetail,
  ScheduleImportRow,
  ScheduleImportRowInput,
  ScheduleImportStatus,
  Vehicle,
  VehicleAssignment,
} from "../api/types";

const DAY_PATTERNS: DayPattern[] = [
  "mon-thu", "fri", "sat", "sun", "mon-fri", "sat-sun", "daily", "custom",
];

function hhmmToMin(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

function minToHHMM(min: number): string {
  min = ((min % 1440) + 1440) % 1440;
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

const STATUS_FILTERS: Array<{ value: ScheduleImportStatus | ""; label: string }> = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "validated", label: "Validated" },
  { value: "rejected", label: "Rejected" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

const EDITABLE_STATUSES: ScheduleImportStatus[] = ["validated", "pending", "active"];

const ASSIGNMENT_STATUSES = ["scheduled", "active", "completed", "cancelled"] as const;
const DUTY_STATUSES = ["scheduled", "active", "completed", "no_show", "cancelled"] as const;

function unwrap<T>(value: ListResponse<T> | T[] | undefined | null): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray((value as ListResponse<T>).data)) {
    return (value as ListResponse<T>).data;
  }
  return [];
}

function toDateTimeLocal(iso?: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const DAY_PATTERN_LABELS: Record<string, string> = {
  "mon-thu": "Mon–Thu", fri: "Fri", sat: "Sat", sun: "Sun",
  "mon-fri": "Mon–Fri", "sat-sun": "Sat–Sun", daily: "Daily", custom: "Custom",
};
function dayPatternLabel(dp: string): string {
  return DAY_PATTERN_LABELS[dp] ?? dp;
}

/** Multi-word station name -> initials ("Faiz Ahmed Faiz" -> "FAF"); short
 *  names (e.g. "N-5") pass through unchanged. Mirrors TripsPage's helper. */
function abbreviateStop(text: string): string {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) return text.trim();
  return words.map((w) => w[0]).join("").toUpperCase();
}

/** "Faiz Ahmed Faiz to N-5" -> "FAF to N-5", derived from the real
 *  route_long_name; falls back to the raw name when it doesn't follow the
 *  "X to Y" convention. */
function routeSpan(longName?: string): string {
  if (!longName) return "";
  const m = longName.match(/^(.*?)\s+to\s+(.*)$/i);
  if (m) return `${abbreviateStop(m[1])} to ${abbreviateStop(m[2])}`;
  return longName;
}

function fmtShortDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function SchedulesPage() {
  const { session } = useAuth();
  const role = (session?.role ?? "").toLowerCase().trim();
  const roleKey = role.replace(/[\s-]+/g, "_");
  const canManage = roleKey === "admin" || roleKey === "operations_supervisor" || roleKey === "supervisor";

  const [imports, setImports] = useState<ScheduleImport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ScheduleImportStatus | "">("");

  const [detail, setDetail] = useState<ScheduleImportDetail | null>(null);
  const [audit, setAudit] = useState<ScheduleAuditEvent[] | null>(null);
  const [diffA, setDiffA] = useState<string>("");
  const [diffB, setDiffB] = useState<string>("");
  const [diff, setDiff] = useState<ScheduleDiff | null>(null);

  const [gtfsLoading, setGtfsLoading] = useState(false);

  const [routes, setRoutes] = useState<Route[]>([]);
  useEffect(() => {
    api.listRoutes()
      .then((r) => setRoutes(unwrap(r)))
      .catch(() => setRoutes([]));
  }, []);

  // Route and day pattern are chosen once for the whole page: the demand card
  // shows ridership for them and the generator builds a timetable for them,
  // so they cannot be allowed to disagree.
  const [routeId, setRouteId] = useState("");
  const [dayPattern, setDayPattern] = useState<DayPattern>("mon-thu");
  useEffect(() => {
    setRouteId((v) => v || (routes[0]?.id ?? ""));
  }, [routes]);

  // What the generator currently holds, what demand and the simulation make
  // of it, and bands the demand card asks the generator to take.
  const [draft, setDraft] = useState<GeneratorDraft | null>(null);
  const [draftFit, setDraftFit] = useState<DraftFit | null>(null);
  const [draftSim, setDraftSim] = useState<string | null>(null);
  const [bandRequest, setBandRequest] = useState<{ seq: number; bands: HeadwayBand[] } | null>(null);

  // Shared by the demand card and the simulation, so both read the same
  // riders over the same days and size the same bus.
  const [demandDays, setDemandDays] = useState(28);
  const [plan, setPlan] = useState<PlanParams>(loadPlan);
  useEffect(() => { savePlan(plan); }, [plan]);

  // The timetable running today for this route and day pattern, with its
  // rows, loaded once here for both cards.
  const activeImport = useMemo(
    () =>
      imports
        .filter((i) => i.status === "active" && i.route_id === routeId && i.day_pattern === dayPattern)
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0] ?? null,
    [imports, routeId, dayPattern]
  );
  const [activeRows, setActiveRows] = useState<{ id: string; rows: ScheduleImportRow[] } | null>(null);
  useEffect(() => {
    if (!activeImport) { setActiveRows(null); return; }
    let cancelled = false;
    api.getScheduleImport(activeImport.id)
      .then((res) => { if (!cancelled) setActiveRows({ id: activeImport.id, rows: res.rows }); })
      .catch(() => { if (!cancelled) setActiveRows(null); });
    return () => { cancelled = true; };
  }, [activeImport]);
  const activeTimetable = useMemo<TimetableSource | null>(
    () => (activeImport && activeRows && activeRows.id === activeImport.id ? { label: "Active timetable", rows: activeRows.rows } : null),
    [activeImport, activeRows]
  );
  const applyBands = useCallback((bands: HeadwayBand[]) => {
    setBandRequest((prev) => ({ seq: (prev?.seq ?? 0) + 1, bands }));
    document.getElementById("schedule-generator")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  const routeNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const r of routes) {
      m[r.id] = r.route_short_name || r.route_id;
    }
    return m;
  }, [routes]);

  const recentImports = useMemo(
    () =>
      [...imports]
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
        .slice(0, 6),
    [imports]
  );

  async function loadImports() {
    setLoading(true);
    try {
      const res = await api.listScheduleImports({ status: statusFilter || undefined, limit: 50 });
      setImports(res.data ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load imports");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadImports(); }, [statusFilter]);


  async function openDetail(id: string) {
    setError(null); setAudit(null);
    try {
      const [res, events] = await Promise.all([
        api.getScheduleImport(id),
        api.listScheduleAudit(id).catch(() => [] as ScheduleAuditEvent[]),
      ]);
      setDetail(res); setAudit(events);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Load failed");
    }
  }

  async function runDiff() {
    setError(null); setDiff(null);
    if (!diffA || !diffB || diffA === diffB) { setError("Pick two different imports to diff"); return; }
    try {
      setDiff(await api.diffScheduleImports(diffA, diffB));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Diff failed");
    }
  }

  async function transition(id: string, status: ScheduleImportStatus) {
    if (status === "active" && !confirm("Activate this schedule? It will become the live timetable.")) return;
    if (status === "archived" && !confirm("Archive this schedule?")) return;
    try {
      await api.setScheduleImportStatus(id, status);
      setSuccess(`Status set to ${status}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Status update failed");
    } finally {
      await loadImports();
      if (detail && detail.import.id === id) await openDetail(id);
    }
  }

  async function autoAssign(id: string) {
    const today = new Date().toISOString().slice(0, 10);
    const date = prompt("Service date (YYYY-MM-DD)", today);
    if (!date) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { setError("Date must be YYYY-MM-DD"); return; }
    const overwrite = confirm("Overwrite existing assignments?\nOK = overwrite, Cancel = skip already-assigned");
    try {
      const r: AutoAssignReport = await api.autoAssignFromImport(id, date, overwrite);
      setSuccess(
        `Auto-assign ${date}: ${r.vehicle_assignments_created} vehicle + ${r.driver_duties_created} driver duties · ${r.blocks} blocks · ${r.blocks_skipped} skipped`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Auto-assign failed");
    }
  }

  async function remove(id: string) {
    if (!confirm("Delete this import? Cannot be undone.")) return;
    try {
      await api.deleteScheduleImport(id);
      setSuccess("Deleted");
      if (detail && detail.import.id === id) setDetail(null);
      await loadImports();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  }

  async function downloadGTFS() {
    const agencyId = session?.agencyId;
    if (!agencyId) { setError("No agency in session"); return; }
    setGtfsLoading(true); setError(null);
    try {
      const blob = await api.exportGTFS(agencyId);
      downloadBlob(blob, `gtfs_${agencyId.slice(0, 8)}.zip`);
      setSuccess("GTFS downloaded");
    } catch (err) {
      setError(err instanceof Error ? err.message : "GTFS export failed");
    } finally { setGtfsLoading(false); }
  }

  // Reload detail rows after a row mutation.
  async function reloadDetailRows() {
    if (!detail) return;
    try {
      const fresh = await api.getScheduleImport(detail.import.id);
      setDetail(fresh);
    } catch { /* ignore */ }
  }

  const detailEditable = detail
    ? EDITABLE_STATUSES.includes(detail.import.status as ScheduleImportStatus)
    : false;

  return (
    <div className="page sched-page">
      <div className="sched-header">
        <div>
          <h1>Schedules</h1>
          <p className="secondary-text">Generate timetables from headway bands and sync to GTFS.</p>
        </div>
        <button
          className="btn sched-gtfs-btn"
          onClick={downloadGTFS}
          disabled={gtfsLoading}
          title="Download static GTFS feed as zip"
        >
          {gtfsLoading ? "Exporting…" : "↓ Download live GTFS"}
        </button>
      </div>

      {!canManage && (
        <div className="banner banner-error">
          Your role is <code>{session?.role ?? "unknown"}</code>. Only{" "}
          <code>operations_supervisor</code> or <code>admin</code> can upload or manage schedules.
        </div>
      )}
      {error && <div className="banner banner-error">{error}</div>}
      {success && <div className="banner banner-success">{success}</div>}

      <DemandCard
        routes={routes}
        routeId={routeId}
        onRouteId={setRouteId}
        dayPattern={dayPattern}
        onDayPattern={setDayPattern}
        dayPatterns={DAY_PATTERNS.map((d) => ({ value: d, label: dayPatternLabel(d) }))}
        days={demandDays}
        onDays={setDemandDays}
        plan={plan}
        onPlan={setPlan}
        activeRows={activeTimetable?.rows ?? null}
        draft={canManage ? draft : null}
        canManage={canManage}
        onApplyBands={applyBands}
        onDraftFit={setDraftFit}
      />

      <SimulationCard
        routeId={routeId}
        dayPattern={dayPattern}
        days={demandDays}
        plan={plan}
        onPlan={setPlan}
        active={activeTimetable}
        draft={canManage ? draft : null}
        onDraftSummary={setDraftSim}
      />

      <div className="sched-hero-grid">
        {canManage && (
          <GenerateScheduleCard
            routes={routes}
            routeId={routeId}
            onRouteId={setRouteId}
            dayPattern={dayPattern}
            onDayPattern={setDayPattern}
            onDraft={setDraft}
            bandRequest={bandRequest}
            fit={draftFit}
            simSummary={draftSim}
            onError={setError}
            onSuccess={(msg) => { setSuccess(msg); void loadImports(); }}
          />
        )}

        <aside className="card sched-recent-card">
          <h3>Recent imports</h3>
          <div className="sched-recent-list">
            {recentImports.length === 0 && <div className="empty">— none yet —</div>}
            {recentImports.map((imp) => {
              const route = imp.route_id ? routeNameById[imp.route_id] : undefined;
              const title = `${dayPatternLabel(imp.day_pattern)}${route ? " · " + route : ""}`;
              const badge =
                imp.status === "active" ? "LIVE"
                : imp.status === "archived" ? "ENDED"
                : imp.status === "rejected" ? "REJECTED"
                : "DRAFT";
              const dateLabel =
                imp.status === "archived"
                  ? (imp.archived_at ? `ended ${fmtShortDate(imp.archived_at)}` : "ended")
                  : (imp.effective_from ? `effective ${fmtShortDate(imp.effective_from)}` : "no effective date");
              return (
                <button
                  key={imp.id}
                  type="button"
                  className="sched-recent-row"
                  onClick={() => openDetail(imp.id)}
                  title={imp.filename}
                >
                  <div className="sched-recent-main">
                    <div className="sched-recent-title">{title}</div>
                    <div className="sched-recent-sub">{imp.row_count} trips · {dateLabel}</div>
                  </div>
                  <span className={`sched-badge sched-badge-${badge.toLowerCase()}`}>{badge}</span>
                </button>
              );
            })}
          </div>
        </aside>
      </div>

      <section className="card">
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Imports ({imports.length})</h3>
          <label style={{ marginLeft: "auto" }}>
            Filter status&nbsp;
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as ScheduleImportStatus | "")}>
              {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
        </div>
        {loading && <div className="secondary-text">Loading…</div>}
        <table className="data-table compact">
          <thead>
            <tr>
              <th>Filename</th><th>Day</th><th>Effective</th>
              <th>Rows</th><th>Errors</th><th>Status</th><th>Uploaded</th><th></th>
            </tr>
          </thead>
          <tbody>
            {imports.length === 0 && (
              <tr><td colSpan={8} className="empty">— none —</td></tr>
            )}
            {imports.map((imp) => (
              <tr key={imp.id}>
                <td>
                  <button className="btn ghost" style={{ padding: 0 }} onClick={() => openDetail(imp.id)}>
                    {imp.filename}
                  </button>
                </td>
                <td>{imp.day_pattern}</td>
                <td>{imp.effective_from?.slice(0, 10) ?? "—"} → {imp.effective_to?.slice(0, 10) ?? "—"}</td>
                <td>{imp.row_count}</td>
                <td>{imp.error_count}</td>
                <td><span className={`badge status-${imp.status}`}>{imp.status}</span></td>
                <td>{imp.created_at.replace("T", " ").slice(0, 16)}</td>
                <td>
                  {canManage && (
                    <ImportActions
                      imp={imp}
                      onOpenDetail={openDetail}
                      onTransition={transition}
                      onDelete={remove}
                      onAutoAssign={autoAssign}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {detail && (
        <section className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h3 style={{ margin: 0 }}>
              {detail.import.filename}{" "}
              <span className="secondary-text">({detail.row_total} rows · {detail.import.error_count} errors)</span>
            </h3>
            {detailEditable && (
              <span className="badge status-validated" style={{ fontSize: "0.75em" }}>editable</span>
            )}
            <button className="btn ghost" style={{ marginLeft: "auto" }} onClick={() => { setDetail(null); setAudit(null); }}>
              Close
            </button>
          </div>

          {canManage && detail.import.status === "active" && (
            <ActiveScheduleControls
              onError={setError}
              onSuccess={setSuccess}
            />
          )}

          <div id="schedule-rows-editor">
            <RowsTable
              rows={detail.rows}
              editable={canManage && detailEditable}
              importId={detail.import.id}
              onRowChange={reloadDetailRows}
              onError={setError}
              onSuccess={setSuccess}
            />
          </div>

          {audit && audit.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4>Audit history ({audit.length})</h4>
              <table className="data-table compact">
                <thead>
                  <tr><th>When</th><th>Event</th><th>Actor</th><th>Details</th></tr>
                </thead>
                <tbody>
                  {audit.map((ev) => (
                    <tr key={ev.id}>
                      <td>{ev.occurred_at.replace("T", " ").slice(0, 19)}</td>
                      <td><span className={`badge status-${ev.event}`}>{ev.event}</span></td>
                      <td>{ev.actor_user_id?.slice(0, 8) ?? "—"}</td>
                      <td>
                        <code style={{ fontSize: "0.8em" }}>
                          {typeof ev.details === "string" ? ev.details : JSON.stringify(ev.details ?? "")}
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="card">
        <h3>Compare imports</h3>
        <div className="form-row">
          <label style={{ flex: 1 }}>
            A
            <select value={diffA} onChange={(e) => setDiffA(e.target.value)}>
              <option value="">— pick —</option>
              {imports.map((imp) => <option key={imp.id} value={imp.id}>{imp.filename} · {imp.status}</option>)}
            </select>
          </label>
          <label style={{ flex: 1 }}>
            B
            <select value={diffB} onChange={(e) => setDiffB(e.target.value)}>
              <option value="">— pick —</option>
              {imports.map((imp) => <option key={imp.id} value={imp.id}>{imp.filename} · {imp.status}</option>)}
            </select>
          </label>
          <button className="btn primary" type="button" onClick={runDiff}>Compare</button>
        </div>
        {diff && (
          <div style={{ marginTop: 12 }}>
            <p>
              Added <strong>{diff.summary.added}</strong> · Removed <strong>{diff.summary.removed}</strong> ·
              Changed <strong>{diff.summary.changed}</strong> · A={diff.summary.a_rows} · B={diff.summary.b_rows}
            </p>
            {diff.added.length > 0 && (
              <>
                <h4 style={{ color: "#16a34a" }}>Added in B ({diff.added.length})</h4>
                <RowsTable rows={diff.added.slice(0, 50)} editable={false} importId="" onRowChange={() => {}} />
              </>
            )}
            {diff.removed.length > 0 && (
              <>
                <h4 style={{ color: "#dc2626" }}>Removed from A ({diff.removed.length})</h4>
                <RowsTable rows={diff.removed.slice(0, 50)} editable={false} importId="" onRowChange={() => {}} />
              </>
            )}
            {diff.changed.length > 0 && (
              <>
                <h4 style={{ color: "#d97706" }}>Changed in B ({diff.changed.length})</h4>
                <RowsTable rows={diff.changed.slice(0, 50)} editable={false} importId="" onRowChange={() => {}} />
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function ActiveScheduleControls({
  onError,
  onSuccess,
}: {
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serviceDate, setServiceDate] = useState(() => new Date().toISOString().slice(0, 10));

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [assignments, setAssignments] = useState<VehicleAssignment[]>([]);
  const [duties, setDuties] = useState<DriverDuty[]>([]);

  const [vehicleStatusEdits, setVehicleStatusEdits] = useState<Record<string, string>>({});
  const [dutyStatusEdits, setDutyStatusEdits] = useState<Record<string, string>>({});

  const [vehicleForm, setVehicleForm] = useState({
    vehicle_id: "",
    block_id: "",
    status: "scheduled",
    notes: "",
  });

  const [dutyForm, setDutyForm] = useState({
    driver_id: "",
    block_id: "",
    duty_type: "regular",
    scheduled_start: "",
    scheduled_end: "",
    status: "scheduled",
    notes: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vehicleRes, driverRes, assignmentRes, dutyRes] = await Promise.all([
        api.listVehicles().catch(() => null),
        api.listDrivers().catch(() => null),
        api.listVehicleAssignments({ date: serviceDate }).catch(() => null),
        api.listDriverDuties({ date: serviceDate }).catch(() => null),
      ]);

      const nextVehicles = unwrap(vehicleRes);
      const nextDrivers = unwrap(driverRes);
      const nextAssignments = unwrap(assignmentRes);
      const nextDuties = unwrap(dutyRes);

      setVehicles(nextVehicles);
      setDrivers(nextDrivers);
      setAssignments(nextAssignments);
      setDuties(nextDuties);
      setVehicleStatusEdits(
        Object.fromEntries(nextAssignments.map((a) => [a.id, a.status || "scheduled"]))
      );
      setDutyStatusEdits(
        Object.fromEntries(nextDuties.map((d) => [d.id, d.status || "scheduled"]))
      );
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to load manual controls");
    } finally {
      setLoading(false);
    }
  }, [onError, serviceDate]);

  useEffect(() => {
    if (expanded) {
      void load();
    }
  }, [expanded, load]);

  async function createVehicleAssignment(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.createVehicleAssignment({
        vehicle_id: vehicleForm.vehicle_id.trim(),
        block_id: vehicleForm.block_id.trim(),
        service_date: serviceDate,
        status: vehicleForm.status,
        notes: vehicleForm.notes.trim() || undefined,
      });
      setVehicleForm((s) => ({ ...s, block_id: "", notes: "" }));
      onSuccess("Vehicle assignment created");
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to create vehicle assignment");
    } finally {
      setBusy(false);
    }
  }

  async function createDriverDuty(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.createDriverDuty({
        driver_id: dutyForm.driver_id.trim(),
        block_id: dutyForm.block_id.trim() || undefined,
        service_date: serviceDate,
        duty_type: dutyForm.duty_type,
        scheduled_start: dutyForm.scheduled_start,
        scheduled_end: dutyForm.scheduled_end,
        status: dutyForm.status,
        notes: dutyForm.notes.trim() || undefined,
      });
      setDutyForm((s) => ({ ...s, block_id: "", notes: "" }));
      onSuccess("Driver duty created");
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to create driver duty");
    } finally {
      setBusy(false);
    }
  }

  async function saveAssignmentStatus(id: string) {
    const status = vehicleStatusEdits[id];
    if (!status) return;
    setBusy(true);
    try {
      await api.updateVehicleAssignment(id, { status });
      onSuccess("Vehicle assignment updated");
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to update vehicle assignment");
    } finally {
      setBusy(false);
    }
  }

  async function saveDutyStatus(id: string) {
    const status = dutyStatusEdits[id];
    if (!status) return;
    setBusy(true);
    try {
      await api.updateDriverDuty(id, { status });
      onSuccess("Driver duty updated");
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to update driver duty");
    } finally {
      setBusy(false);
    }
  }

  async function removeAssignment(id: string) {
    if (!confirm("Delete this vehicle assignment?")) return;
    setBusy(true);
    try {
      await api.deleteVehicleAssignment(id);
      onSuccess("Vehicle assignment deleted");
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to delete vehicle assignment");
    } finally {
      setBusy(false);
    }
  }

  async function removeDuty(id: string) {
    if (!confirm("Delete this driver duty?")) return;
    setBusy(true);
    try {
      await api.deleteDriverDuty(id);
      onSuccess("Driver duty deleted");
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to delete driver duty");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" style={{ marginTop: 12 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          className="btn"
          type="button"
          onClick={() => document.getElementById("schedule-rows-editor")?.scrollIntoView({ behavior: "smooth", block: "start" })}
        >
          Edit Schedule
        </button>
        <button className="btn" type="button" onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Hide Manual Override" : "Manual Override"}
        </button>
        {!expanded && (
          <button className="btn primary" type="button" onClick={() => setExpanded(true)}>
            Assign Driver/Vehicle
          </button>
        )}
      </div>

      {expanded && (
        <>
          <div className="form-row" style={{ marginTop: 10 }}>
            <label>
              Service date
              <input
                type="date"
                value={serviceDate}
                onChange={(e) => setServiceDate(e.target.value)}
              />
            </label>
            <button className="btn ghost" type="button" onClick={() => void load()} disabled={loading || busy}>
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          <div className="grid-2" style={{ marginTop: 12 }}>
            <div className="card">
              <h4>Assign vehicle</h4>
              <form className="form-grid" onSubmit={createVehicleAssignment}>
                <div className="form-row">
                  <label>
                    Vehicle
                    <input
                      list="schedule-vehicles"
                      value={vehicleForm.vehicle_id}
                      onChange={(e) => setVehicleForm((s) => ({ ...s, vehicle_id: e.target.value }))}
                      required
                    />
                  </label>
                  <label>
                    Block ID
                    <input
                      value={vehicleForm.block_id}
                      onChange={(e) => setVehicleForm((s) => ({ ...s, block_id: e.target.value }))}
                      required
                    />
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    Status
                    <select
                      value={vehicleForm.status}
                      onChange={(e) => setVehicleForm((s) => ({ ...s, status: e.target.value }))}
                    >
                      {ASSIGNMENT_STATUSES.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Notes
                    <input
                      value={vehicleForm.notes}
                      onChange={(e) => setVehicleForm((s) => ({ ...s, notes: e.target.value }))}
                    />
                  </label>
                </div>
                <button className="btn primary" type="submit" disabled={busy || loading}>Create vehicle assignment</button>
              </form>
            </div>

            <div className="card">
              <h4>Assign driver</h4>
              <form className="form-grid" onSubmit={createDriverDuty}>
                <div className="form-row">
                  <label>
                    Driver
                    <input
                      list="schedule-drivers"
                      value={dutyForm.driver_id}
                      onChange={(e) => setDutyForm((s) => ({ ...s, driver_id: e.target.value }))}
                      required
                    />
                  </label>
                  <label>
                    Block ID (optional)
                    <input
                      value={dutyForm.block_id}
                      onChange={(e) => setDutyForm((s) => ({ ...s, block_id: e.target.value }))}
                    />
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    Scheduled start
                    <input
                      type="datetime-local"
                      value={dutyForm.scheduled_start}
                      onChange={(e) => setDutyForm((s) => ({ ...s, scheduled_start: e.target.value }))}
                      required
                    />
                  </label>
                  <label>
                    Scheduled end
                    <input
                      type="datetime-local"
                      value={dutyForm.scheduled_end}
                      onChange={(e) => setDutyForm((s) => ({ ...s, scheduled_end: e.target.value }))}
                      required
                    />
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    Status
                    <select
                      value={dutyForm.status}
                      onChange={(e) => setDutyForm((s) => ({ ...s, status: e.target.value }))}
                    >
                      {DUTY_STATUSES.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Notes
                    <input
                      value={dutyForm.notes}
                      onChange={(e) => setDutyForm((s) => ({ ...s, notes: e.target.value }))}
                    />
                  </label>
                </div>
                <button className="btn primary" type="submit" disabled={busy || loading}>Create driver duty</button>
              </form>
            </div>
          </div>

          <datalist id="schedule-vehicles">
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{v.vehicle_id}</option>
            ))}
          </datalist>
          <datalist id="schedule-drivers">
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>{d.employee_id}</option>
            ))}
          </datalist>

          <div className="grid-2" style={{ marginTop: 12 }}>
            <div className="card">
              <h4>Vehicle assignments ({assignments.length})</h4>
              <table className="data-table compact">
                <thead>
                  <tr><th>Vehicle</th><th>Block</th><th>Status</th><th></th></tr>
                </thead>
                <tbody>
                  {assignments.length === 0 && (
                    <tr><td colSpan={4} className="empty">— none —</td></tr>
                  )}
                  {assignments.map((a) => (
                    <tr key={a.id}>
                      <td>{a.vehicle_id.slice(0, 8)}…</td>
                      <td>{a.block_id.slice(0, 8)}…</td>
                      <td>
                        <select
                          value={vehicleStatusEdits[a.id] ?? a.status}
                          onChange={(e) => setVehicleStatusEdits((s) => ({ ...s, [a.id]: e.target.value }))}
                        >
                          {ASSIGNMENT_STATUSES.map((status) => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="btn ghost" type="button" onClick={() => void saveAssignmentStatus(a.id)} disabled={busy}>Save</button>
                          <button className="btn ghost" type="button" onClick={() => void removeAssignment(a.id)} disabled={busy} style={{ color: "#dc2626" }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card">
              <h4>Driver duties ({duties.length})</h4>
              <table className="data-table compact">
                <thead>
                  <tr><th>Driver</th><th>Start</th><th>Status</th><th></th></tr>
                </thead>
                <tbody>
                  {duties.length === 0 && (
                    <tr><td colSpan={4} className="empty">— none —</td></tr>
                  )}
                  {duties.map((d) => (
                    <tr key={d.id}>
                      <td>{d.driver_id.slice(0, 8)}…</td>
                      <td>{toDateTimeLocal(d.scheduled_start).replace("T", " ") || "—"}</td>
                      <td>
                        <select
                          value={dutyStatusEdits[d.id] ?? d.status}
                          onChange={(e) => setDutyStatusEdits((s) => ({ ...s, [d.id]: e.target.value }))}
                        >
                          {DUTY_STATUSES.map((status) => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="btn ghost" type="button" onClick={() => void saveDutyStatus(d.id)} disabled={busy}>Save</button>
                          <button className="btn ghost" type="button" onClick={() => void removeDuty(d.id)} disabled={busy} style={{ color: "#dc2626" }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

// ── RowsTable ─────────────────────────────────────────────────────────────────

interface RowsTableProps {
  rows: ScheduleImportRow[];
  editable: boolean;
  importId: string;
  onRowChange: () => void;
  onError?: (msg: string) => void;
  onSuccess?: (msg: string) => void;
}

function blankRow(): ScheduleImportRowInput {
  return { shift_number: null, start_time: "", direction: "", trip_duration_min: null, operational_state: "", error: "" };
}

function RowsTable({ rows, editable, importId, onRowChange, onError, onSuccess }: RowsTableProps) {
  // Local row copies for editing: keyed by row_no (string so new rows use "new").
  const [drafts, setDrafts] = useState<Record<string, ScheduleImportRowInput>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [addingRow, setAddingRow] = useState(false);
  const [newDraft, setNewDraft] = useState<ScheduleImportRowInput>(blankRow());

  // Reset drafts when rows change (e.g. after reload).
  const prevRowsRef = useRef(rows);
  useEffect(() => {
    if (prevRowsRef.current !== rows) {
      setDrafts({});
      prevRowsRef.current = rows;
    }
  }, [rows]);

  const getDraft = useCallback((row: ScheduleImportRow): ScheduleImportRowInput => {
    const key = String(row.row_no);
    return drafts[key] ?? {
      shift_number: row.shift_number ?? null,
      start_time: row.start_time ?? "",
      direction: row.direction ?? "",
      trip_duration_min: row.trip_duration_min ?? null,
      operational_state: row.operational_state ?? "",
      error: row.error ?? "",
    };
  }, [drafts]);

  function patchDraft(rowNo: number, patch: Partial<ScheduleImportRowInput>) {
    const key = String(rowNo);
    setDrafts((prev) => {
      const base = prev[key] ?? getDraft(rows.find((r) => r.row_no === rowNo)!);
      return { ...prev, [key]: { ...base, ...patch } };
    });
  }

  async function saveRow(row: ScheduleImportRow) {
    const key = String(row.row_no);
    const draft = drafts[key];
    if (!draft) return; // nothing changed
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.updateImportRow(importId, row.row_no, draft);
      onSuccess?.(`Row ${row.row_no} saved`);
      onRowChange();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : `Save row ${row.row_no} failed`);
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  }

  async function deleteRow(row: ScheduleImportRow) {
    if (!confirm(`Delete row ${row.row_no}?`)) return;
    const key = String(row.row_no);
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.deleteImportRow(importId, row.row_no);
      onSuccess?.(`Row ${row.row_no} deleted`);
      onRowChange();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : `Delete row ${row.row_no} failed`);
      setSaving((s) => ({ ...s, [key]: false }));
    }
  }

  async function addRow() {
    setSaving((s) => ({ ...s, new: true }));
    try {
      await api.addImportRow(importId, newDraft);
      onSuccess?.("Row added");
      setNewDraft(blankRow());
      setAddingRow(false);
      onRowChange();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : "Add row failed");
    } finally {
      setSaving((s) => ({ ...s, new: false }));
    }
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="data-table compact">
        <thead>
          <tr>
            <th>#</th>
            <th>Shift</th>
            <th>Start</th>
            <th>Direction</th>
            <th>Duration (min)</th>
            <th>State</th>
            <th>Error</th>
            {editable && <th></th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && !addingRow && (
            <tr><td colSpan={editable ? 8 : 7} className="empty">— no rows —</td></tr>
          )}
          {rows.map((row) => {
            const key = String(row.row_no);
            const draft = getDraft(row);
            const dirty = !!drafts[key];
            const busy = saving[key] ?? false;

            if (!editable) {
              return (
                <tr key={row.row_no} className={row.error ? "row-error" : undefined}>
                  <td>{row.row_no}</td>
                  <td>{row.shift_number ?? "—"}</td>
                  <td>{row.start_time || "—"}</td>
                  <td>{row.direction || "—"}</td>
                  <td>{row.trip_duration_min ?? "—"}</td>
                  <td>{row.operational_state || "—"}</td>
                  <td style={{ color: "#c00" }}>{row.error || ""}</td>
                </tr>
              );
            }

            return (
              <tr key={row.row_no} className={row.error ? "row-error" : undefined}>
                <td style={{ color: "var(--text-3)" }}>{row.row_no}</td>
                <td>
                  <input
                    type="number"
                    style={inlineInput}
                    value={draft.shift_number ?? ""}
                    placeholder="—"
                    onChange={(e) => patchDraft(row.row_no, { shift_number: e.target.value === "" ? null : Number(e.target.value) })}
                    disabled={busy}
                  />
                </td>
                <td>
                  <input
                    style={inlineInput}
                    value={draft.start_time}
                    placeholder="HH:MM"
                    onChange={(e) => patchDraft(row.row_no, { start_time: e.target.value })}
                    disabled={busy}
                  />
                </td>
                <td>
                  <input
                    style={inlineInput}
                    value={draft.direction}
                    placeholder="direction"
                    onChange={(e) => patchDraft(row.row_no, { direction: e.target.value })}
                    disabled={busy}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    style={inlineInput}
                    value={draft.trip_duration_min ?? ""}
                    placeholder="—"
                    onChange={(e) => patchDraft(row.row_no, { trip_duration_min: e.target.value === "" ? null : Number(e.target.value) })}
                    disabled={busy}
                  />
                </td>
                <td>
                  <input
                    style={inlineInput}
                    value={draft.operational_state}
                    placeholder="state"
                    onChange={(e) => patchDraft(row.row_no, { operational_state: e.target.value })}
                    disabled={busy}
                  />
                </td>
                <td style={{ color: "#c00", fontSize: "0.8em" }}>{row.error || ""}</td>
                <td>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      className="btn primary"
                      style={{ padding: "2px 8px", fontSize: "0.8em" }}
                      onClick={() => saveRow(row)}
                      disabled={busy || !dirty}
                      title={dirty ? "Save this row" : "No changes"}
                    >
                      {busy ? "…" : "Save"}
                    </button>
                    <button
                      className="btn ghost"
                      style={{ padding: "2px 8px", fontSize: "0.8em", color: "#dc2626" }}
                      onClick={() => deleteRow(row)}
                      disabled={busy}
                      title="Delete row"
                    >
                      ✕
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}

          {/* New row form */}
          {editable && addingRow && (
            <tr>
              <td style={{ color: "var(--text-3)" }}>new</td>
              <td>
                <input
                  type="number"
                  style={inlineInput}
                  value={newDraft.shift_number ?? ""}
                  placeholder="—"
                  onChange={(e) => setNewDraft((d) => ({ ...d, shift_number: e.target.value === "" ? null : Number(e.target.value) }))}
                  disabled={saving.new}
                />
              </td>
              <td>
                <input
                  style={inlineInput}
                  value={newDraft.start_time}
                  placeholder="HH:MM"
                  onChange={(e) => setNewDraft((d) => ({ ...d, start_time: e.target.value }))}
                  disabled={saving.new}
                  autoFocus
                />
              </td>
              <td>
                <input
                  style={inlineInput}
                  value={newDraft.direction}
                  placeholder="direction"
                  onChange={(e) => setNewDraft((d) => ({ ...d, direction: e.target.value }))}
                  disabled={saving.new}
                />
              </td>
              <td>
                <input
                  type="number"
                  style={inlineInput}
                  value={newDraft.trip_duration_min ?? ""}
                  placeholder="—"
                  onChange={(e) => setNewDraft((d) => ({ ...d, trip_duration_min: e.target.value === "" ? null : Number(e.target.value) }))}
                  disabled={saving.new}
                />
              </td>
              <td>
                <input
                  style={inlineInput}
                  value={newDraft.operational_state}
                  placeholder="state"
                  onChange={(e) => setNewDraft((d) => ({ ...d, operational_state: e.target.value }))}
                  disabled={saving.new}
                />
              </td>
              <td />
              <td>
                <div style={{ display: "flex", gap: 4 }}>
                  <button
                    className="btn primary"
                    style={{ padding: "2px 8px", fontSize: "0.8em" }}
                    onClick={addRow}
                    disabled={saving.new}
                  >
                    {saving.new ? "…" : "Add"}
                  </button>
                  <button
                    className="btn ghost"
                    style={{ padding: "2px 8px", fontSize: "0.8em" }}
                    onClick={() => { setAddingRow(false); setNewDraft(blankRow()); }}
                    disabled={saving.new}
                  >
                    Cancel
                  </button>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {editable && !addingRow && (
        <button
          className="btn"
          style={{ marginTop: 8, fontSize: "0.85em" }}
          onClick={() => setAddingRow(true)}
        >
          + Add Trip
        </button>
      )}
    </div>
  );
}

const inlineInput: React.CSSProperties = {
  width: "100%",
  minWidth: 60,
  maxWidth: 120,
  padding: "2px 4px",
  fontSize: "0.85em",
  border: "1px solid var(--border)",
  borderRadius: 3,
  background: "var(--bg-2)",
  color: "inherit",
};

// ── ImportActions ─────────────────────────────────────────────────────────────

function ImportActions({
  imp,
  onOpenDetail,
  onTransition,
  onDelete,
  onAutoAssign,
}: {
  imp: ScheduleImport;
  onOpenDetail: (id: string) => void;
  onTransition: (id: string, status: ScheduleImportStatus) => void;
  onDelete: (id: string) => void;
  onAutoAssign: (id: string) => void;
}) {
  const canActivate = imp.status === "validated" || imp.status === "archived";
  const canArchive = imp.status === "active" || imp.status === "validated";
  const canDelete = imp.status !== "active";
  const canAssign = imp.status === "active";
  const canEdit = imp.status === "active" || imp.status === "validated" || imp.status === "pending";
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {canEdit && <button className="btn" onClick={() => onOpenDetail(imp.id)}>Edit</button>}
      {canActivate && <button className="btn primary" onClick={() => onTransition(imp.id, "active")}>Activate</button>}
      {canAssign && <button className="btn" onClick={() => onAutoAssign(imp.id)}>Auto-assign</button>}
      {canArchive && <button className="btn" onClick={() => onTransition(imp.id, "archived")}>Archive</button>}
      {canDelete && <button className="btn ghost" onClick={() => onDelete(imp.id)}>Delete</button>}
    </div>
  );
}

// ── GenerateScheduleCard ─────────────────────────────────────────────────────

const DEFAULT_BANDS: HeadwayBand[] = [
  { from_min: 6 * 60, to_min: 9 * 60, headway_min: 10 },
  { from_min: 9 * 60, to_min: 16 * 60, headway_min: 15 },
  { from_min: 16 * 60, to_min: 22 * 60, headway_min: 10 },
];

function GenerateScheduleCard({
  routes,
  routeId,
  onRouteId: setRouteId,
  dayPattern,
  onDayPattern: setDayPattern,
  onDraft,
  bandRequest,
  fit,
  simSummary,
  onError,
  onSuccess,
}: {
  routes: Route[];
  routeId: string;
  onRouteId: (id: string) => void;
  dayPattern: DayPattern;
  onDayPattern: (p: DayPattern) => void;
  /** Reports the form's current timetable so demand can be set against it. */
  onDraft: (draft: GeneratorDraft) => void;
  /** Bands handed over from the demand card; a new seq means "apply these". */
  bandRequest: { seq: number; bands: HeadwayBand[] } | null;
  /** How the draft stands against ridership, when there is ridership to compare. */
  fit: DraftFit | null;
  /** The simulation card's one-line verdict on the draft, when it has run it. */
  simSummary: string | null;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}) {
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [effectiveTo, setEffectiveTo] = useState("");
  const [serviceStart, setServiceStart] = useState("06:00");
  const [serviceEnd, setServiceEnd] = useState("22:00");
  const [turnaround, setTurnaround] = useState(5);
  const [bands, setBands] = useState<HeadwayBand[]>(DEFAULT_BANDS);
  const [blockCountOverride, setBlockCountOverride] = useState<string>("");
  const [anchor, setAnchor] = useState<"upward" | "downward">("upward");
  // Alternating is what the generator has always done, so it stays the
  // default; taking bands from the demand card switches to both directions,
  // because those bands are sized per direction.
  const [dispatchMode, setDispatchMode] = useState<DispatchMode>("alternating");
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState<GenerationResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [showEffectiveToPicker, setShowEffectiveToPicker] = useState(false);

  const appliedSeq = useRef(0);
  useEffect(() => {
    if (!bandRequest || bandRequest.seq === appliedSeq.current) return;
    appliedSeq.current = bandRequest.seq;
    setBands(bandRequest.bands);
    setDispatchMode("both_directions");
  }, [bandRequest]);

  function buildParams(showErrors: boolean): GenerateScheduleParams | null {
    const fail = (msg: string) => { if (showErrors) onError(msg); return null; };
    if (!routeId.trim()) return fail("Pick a route");
    const ss = hhmmToMin(serviceStart);
    const se = hhmmToMin(serviceEnd);
    if (ss == null || se == null) return fail("Service window times must be HH:MM");
    if (se <= ss) return fail("Service end must be after service start");
    if (bands.length === 0) return fail("Add at least one headway band");
    if (turnaround < 0) return fail("Turnaround must be ≥ 0");
    const blockCount = blockCountOverride.trim()
      ? Math.max(1, Math.floor(Number(blockCountOverride)))
      : null;
    return {
      route_id: routeId,
      day_pattern: dayPattern,
      effective_from: effectiveFrom || null,
      effective_to: effectiveTo || null,
      service_start_min: ss,
      service_end_min: se,
      turnaround_min: turnaround,
      bands,
      block_count: blockCount,
      anchor_direction: anchor,
      // Left out for the default so the request — and the hash the server
      // files the timetable under — is what it always was.
      dispatch_mode: dispatchMode === "both_directions" ? dispatchMode : undefined,
      notes: notes.trim() || undefined,
    };
  }

  // Silently keep the live "Generate N trips" count in sync with the real
  // generator output as the form changes, without spamming error banners
  // for an incomplete in-progress form.
  useEffect(() => {
    const p = buildParams(false);
    if (!p) { setPreview(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.previewGeneratedSchedule(p)
        .then((res) => { if (!cancelled) setPreview(res); })
        .catch(() => { if (!cancelled) setPreview(null); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, dayPattern, effectiveFrom, effectiveTo, serviceStart, serviceEnd, turnaround, bands, blockCountOverride, anchor, dispatchMode]);

  // Keep the page's copy of the draft current. The preview is only a draft of
  // *this* form while its params still match; a stale one is reported as no
  // timetable rather than as the wrong one.
  useEffect(() => {
    const matches = !!preview && preview.params.route_id === routeId && preview.params.day_pattern === dayPattern;
    onDraft({
      rows: matches ? preview.rows : null,
      bands,
      serviceStartMin: hhmmToMin(serviceStart),
      serviceEndMin: hhmmToMin(serviceEnd),
      roundTripMin: matches ? preview.round_trip_min : null,
    });
  }, [preview, bands, serviceStart, serviceEnd, routeId, dayPattern, onDraft]);

  async function doPreview() {
    const p = buildParams(true);
    if (!p) return;
    setBusy(true);
    try {
      const res = await api.previewGeneratedSchedule(p);
      setPreview(res);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Preview failed");
    } finally {
      setBusy(false);
    }
  }

  async function doCommit(activate: boolean) {
    const p = buildParams(true);
    if (!p) return;
    setBusy(true);
    try {
      const res = await api.commitGeneratedSchedule(p, activate);
      onSuccess(
        activate
          ? `Generated schedule activated (${res.summary.trips_total} trips, ${res.summary.block_count} blocks)`
          : `Generated schedule saved (${res.summary.trips_total} trips). Activate from the imports table when ready.`
      );
      setPreview(res.summary);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  function updateBand(idx: number, patch: Partial<HeadwayBand>) {
    setBands((prev) => prev.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  }

  function addBand() {
    const last = bands[bands.length - 1];
    const from = last ? last.to_min : hhmmToMin(serviceStart) ?? 360;
    const to = Math.min((hhmmToMin(serviceEnd) ?? 1320), from + 60);
    setBands([...bands, { from_min: from, to_min: to, headway_min: 15 }]);
  }

  function removeBand(idx: number) {
    setBands((prev) => prev.filter((_, i) => i !== idx));
  }

  return (
    <section className="card" id="schedule-generator">
      <h3>Generate timetable</h3>
      <p className="secondary-text" style={{ marginTop: 0 }}>
        No file upload. Pick a route + service window, define headway bands, and the system
        builds the trips and syncs to GTFS automatically.
      </p>

      {routes.length === 0 && (
        <div className="banner banner-error" style={{ marginBottom: 12 }}>
          No routes defined yet. Add a route, stops, and a per-direction stop template in{" "}
          <a href="/stop-templates" style={{ textDecoration: "underline" }}>Network</a>{" "}
          before generating a schedule.
        </div>
      )}

      <div className="sched-form-row">
        <label className="sched-field" style={{ flex: "2 1 260px" }}>
          <span>Route</span>
          <select value={routeId} onChange={(e) => setRouteId(e.target.value)}>
            <option value="">— pick a route —</option>
            {routes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.route_short_name || r.route_id}
                {r.route_long_name ? ` — ${routeSpan(r.route_long_name)}` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="sched-field">
          <span>Day pattern</span>
          <select value={dayPattern} onChange={(e) => setDayPattern(e.target.value as DayPattern)}>
            {DAY_PATTERNS.map((d) => <option key={d} value={d}>{dayPatternLabel(d)}</option>)}
          </select>
        </label>
        <label className="sched-field">
          <span>Anchor direction</span>
          <div className="sched-segmented">
            <button
              type="button"
              className={anchor === "upward" ? "active" : ""}
              onClick={() => setAnchor("upward")}
            >
              Upward
            </button>
            <button
              type="button"
              className={anchor === "downward" ? "active" : ""}
              onClick={() => setAnchor("downward")}
            >
              Downward
            </button>
          </div>
        </label>
        <label className="sched-field">
          <span>Dispatch</span>
          <div className="sched-segmented">
            <button
              type="button"
              className={dispatchMode === "alternating" ? "active" : ""}
              onClick={() => setDispatchMode("alternating")}
              title="One trip per headway, direction alternating block by block. The headway is the gap between departures anywhere on the route."
            >
              Alternating
            </button>
            <button
              type="button"
              className={dispatchMode === "both_directions" ? "active" : ""}
              onClick={() => setDispatchMode("both_directions")}
              title="A trip leaves each terminal on every headway. The headway is what a rider gets in each direction."
            >
              Both directions
            </button>
          </div>
        </label>
      </div>

      <div className="sched-form-row">
        <label className="sched-field">
          <span>Effective from</span>
          <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
        </label>
        <label className="sched-field">
          <span>Effective to</span>
          {effectiveTo || showEffectiveToPicker ? (
            <input
              type="date"
              value={effectiveTo}
              autoFocus={showEffectiveToPicker && !effectiveTo}
              onChange={(e) => setEffectiveTo(e.target.value)}
              onBlur={() => { if (!effectiveTo) setShowEffectiveToPicker(false); }}
            />
          ) : (
            <button type="button" className="sched-open-ended-btn" onClick={() => setShowEffectiveToPicker(true)}>
              open-ended
            </button>
          )}
        </label>
        <label className="sched-field">
          <span>Service start</span>
          <input value={serviceStart} onChange={(e) => setServiceStart(e.target.value)} placeholder="06:00" />
        </label>
        <label className="sched-field">
          <span>Service end</span>
          <input value={serviceEnd} onChange={(e) => setServiceEnd(e.target.value)} placeholder="22:00" />
        </label>
        <label className="sched-field">
          <span>Turnaround</span>
          <div className="sched-suffix-field">
            <input
              type="number" min={0} max={120}
              value={turnaround}
              onChange={(e) => setTurnaround(Math.max(0, Number(e.target.value) || 0))}
            />
            <span className="sched-suffix">min</span>
          </div>
        </label>
      </div>

      <div className="sched-bands-head">
        <h4>Headway bands</h4>
        <button className="sched-add-band" type="button" onClick={addBand}>+ Add band</button>
      </div>
      <div className="sched-band-list">
        {bands.map((b, i) => (
          <BandRow
            key={i}
            band={b}
            onChange={(patch) => updateBand(i, patch)}
            onRemove={() => removeBand(i)}
          />
        ))}
      </div>

      {fit && preview && <DraftFitStrip fit={fit} dispatchMode={dispatchMode} simSummary={simSummary} />}

      <details className="sched-advanced">
        <summary>Advanced options</summary>
        <div className="form-row" style={{ marginTop: 10 }}>
          <label>
            Block count (optional override)
            <input
              type="number" min={1}
              value={blockCountOverride}
              onChange={(e) => setBlockCountOverride(e.target.value)}
              placeholder={preview ? String(preview.min_blocks) : "auto"}
              style={{ width: 120 }}
            />
          </label>
          <label style={{ flex: 1 }}>
            Notes
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="optional" />
          </label>
        </div>
      </details>

      <div className="sched-actions-row">
        <button className="btn primary sched-generate-btn" type="button" onClick={() => doCommit(false)} disabled={busy}>
          {busy ? <Spinner label="Generating" /> : `Generate ${preview ? preview.trips_total : ""} trips`.trim()}
        </button>
        <button className="btn sched-preview-btn" type="button" onClick={doPreview} disabled={busy}>
          {busy ? <Spinner label="Computing" /> : "Preview timetable"}
        </button>
        <button className="sched-activate-link" type="button" onClick={() => doCommit(true)} disabled={busy}>
          or save &amp; activate immediately
        </button>
      </div>

      {busy && <IndeterminateBar />}

      {preview && (
        <div className="card" style={{ marginTop: 12 }}>
          <SummaryRow gen={preview} />
          <TimetableGrid rows={preview.rows} blockCount={preview.block_count} />
        </div>
      )}
    </section>
  );
}

// DraftFitStrip — one line, under the bands, on how the draft stands against
// ridership. The full comparison is the demand card's chart; this is the
// answer to "did that edit help?" without scrolling away from the bands.
function DraftFitStrip({ fit, dispatchMode, simSummary }: { fit: DraftFit; dispatchMode: DispatchMode; simSummary: string | null }) {
  const riders = (n: number) => Math.round(n).toLocaleString("en-US");
  const slices = fit.bucketMin === 60 ? "hours" : "half hours";
  const over = fit.overBuckets > 0;
  const worst = fit.worst;
  const when = worst
    ? `${fmtHHMM(worst.startMin)}–${fmtHHMM(worst.startMin + fit.bucketMin)} ${worst.dirName}`
    : "";

  // Riders outside the service window are not a capacity problem — nothing
  // runs for them to be left behind by — so they are said separately.
  const outside: string[] = [];
  if (fit.ridersBefore >= 1) outside.push(`${riders(fit.ridersBefore)} before ${fmtHHMM(fit.serviceStartMin)}`);
  if (fit.ridersAfter >= 1) outside.push(`${riders(fit.ridersAfter)} after ${fmtHHMM(fit.serviceEndMin)}`);
  if (outside.length > 0) outside[0] = outside[0].replace(" ", " riders ");

  return (
    <div className={`sched-fit ${over ? "over" : "ok"}`} role="status">
      <span className="sched-fit-icon" aria-hidden>{over ? "▲" : "✓"}</span>
      <div>
        {!worst ? (
          <strong>No riders travel inside this service window.</strong>
        ) : over ? (
          <>
            <strong>Over capacity in {fit.overBuckets} of {fit.buckets} {slices} of service.</strong>{" "}
            Worst is {when}:{" "}
            {worst.trips === 0
              ? `${riders(worst.peak)} riders and no trip`
              : `${riders(worst.peak)} riders for ${worst.trips} trip${worst.trips === 1 ? "" : "s"} (${fmtPct(worst.loadFactor ?? 0)} full)`}.
            {dispatchMode === "alternating" && (
              <> Alternating dispatch serves each direction in bursts; “Both directions” gives every headway to both.</>
            )}
          </>
        ) : (
          <>
            <strong>Within capacity in every one of the {fit.buckets} {slices} of service.</strong>{" "}
            Fullest is {when} at {fmtPct(worst.loadFactor ?? 0)}.
          </>
        )}
        {outside.length > 0 && (
          <> Each day {outside.join(" and ")} travel outside this service window.</>
        )}{" "}
        <a href="#schedule-demand" onClick={(e) => {
          e.preventDefault();
          document.getElementById("schedule-demand")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}>See demand ↑</a>
        {simSummary && (
          <div className="sched-fit-sim">
            {simSummary}{" "}
            <a href="#schedule-simulation" onClick={(e) => {
              e.preventDefault();
              document.getElementById("schedule-simulation")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}>See the simulation ↑</a>
          </div>
        )}
      </div>
    </div>
  );
}

// Spinner — small CSS-only spinner with a label. Goes inside busy buttons.
function Spinner({ label }: { label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        aria-hidden
        style={{
          width: 12, height: 12,
          border: "2px solid currentColor",
          borderRightColor: "transparent",
          borderRadius: "50%",
          display: "inline-block",
          animation: "occ-spin 0.7s linear infinite",
        }}
      />
      {label}…
    </span>
  );
}

// IndeterminateBar — slim animated bar shown while the generator is
// busy. Pure CSS keyframes; no extra deps.
function IndeterminateBar() {
  return (
    <div
      role="progressbar"
      aria-label="Working"
      style={{
        marginTop: 10,
        height: 3,
        width: "100%",
        background: "var(--bg-2)",
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "30%",
          background: "var(--accent, #4c8bf5)",
          animation: "occ-indeterminate 1.2s ease-in-out infinite",
          borderRadius: 2,
        }}
      />
      <style>{`
        @keyframes occ-spin { to { transform: rotate(360deg); } }
        @keyframes occ-indeterminate {
          0%   { left: -30%; }
          60%  { left: 100%; }
          100% { left: 100%; }
        }
      `}</style>
    </div>
  );
}

function BandRow({
  band, onChange, onRemove,
}: {
  band: HeadwayBand;
  onChange: (patch: Partial<HeadwayBand>) => void;
  onRemove: () => void;
}) {
  // Peak/off-peak is a display-only heuristic derived from the real
  // headway the user entered (tighter headway == busier band); it isn't a
  // distinct backend field.
  const tag = band.headway_min <= 12 ? "peak" : "off-peak";
  return (
    <div className="sched-band-row">
      <span className="sched-band-label">from</span>
      <input
        className="sched-band-time"
        value={minToHHMM(band.from_min)}
        onChange={(e) => {
          const v = hhmmToMin(e.target.value);
          if (v != null) onChange({ from_min: v });
        }}
      />
      <span className="sched-band-label">to</span>
      <input
        className="sched-band-time"
        value={minToHHMM(band.to_min)}
        onChange={(e) => {
          const v = hhmmToMin(e.target.value);
          if (v != null) onChange({ to_min: v });
        }}
      />
      <span className="sched-band-label">every</span>
      <input
        type="number" min={1} max={120} className="sched-band-num"
        value={band.headway_min}
        onChange={(e) => onChange({ headway_min: Math.max(1, Number(e.target.value) || 1) })}
      />
      <span className="sched-band-label">min</span>
      <span className={`sched-band-tag ${tag === "peak" ? "peak" : "offpeak"}`}>{tag}</span>
      <button className="sched-band-remove" type="button" onClick={onRemove} title="Remove band">✕</button>
    </div>
  );
}

function SummaryRow({ gen }: { gen: GenerationResult }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: "0.9em", marginBottom: 12 }}>
      <span><strong>{gen.trips_total}</strong> trips</span>
      <span><strong>{gen.block_count}</strong> blocks (min {gen.min_blocks})</span>
      <span>round-trip <strong>{gen.round_trip_min}</strong> min</span>
      <span>up <strong>{gen.upward_duration_min}</strong> · down <strong>{gen.downward_duration_min}</strong> min</span>
      <code style={{ marginLeft: "auto", color: "var(--text-3)" }}>sha {gen.sha256.slice(0, 12)}…</code>
    </div>
  );
}

// TimetableGrid — compact "blocks × time-of-day" view. Each cell is a tick
// (▲ upward, ▼ downward) at its start-time column. Read-only after generate;
// fine-grained edits happen by opening the persisted import in the detail
// editor below.
function TimetableGrid({ rows, blockCount }: { rows: ScheduleImportRow[]; blockCount: number }) {
  const { minH, maxH } = useMemo(() => {
    let mn = 23, mx = 0;
    for (const r of rows) {
      const m = hhmmToMin(r.start_time);
      if (m == null) continue;
      const h = Math.floor(m / 60);
      if (h < mn) mn = h;
      if (h > mx) mx = h;
    }
    return { minH: Math.max(0, mn), maxH: Math.min(23, mx) };
  }, [rows]);

  const hourSpan = maxH - minH + 1;
  const pxPerMin = 2;
  const gridWidth = hourSpan * 60 * pxPerMin;

  return (
    <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: 6 }}>
      <div style={{ position: "relative", minWidth: gridWidth + 80 }}>
        {/* Header */}
        <div style={{ display: "flex", borderBottom: "1px solid var(--border)" }}>
          <div style={{ width: 80, padding: "4px 8px", fontSize: "0.75em", color: "var(--text-3)" }}>block</div>
          <div style={{ position: "relative", flex: 1, height: 24 }}>
            {Array.from({ length: hourSpan + 1 }).map((_, i) => {
              const h = minH + i;
              return (
                <div
                  key={i}
                  style={{
                    position: "absolute",
                    left: i * 60 * pxPerMin,
                    top: 0,
                    fontSize: "0.7em",
                    color: "var(--text-3)",
                    transform: "translateX(-50%)",
                  }}
                >
                  {String(h).padStart(2, "0")}:00
                </div>
              );
            })}
          </div>
        </div>
        {/* Block rows */}
        {Array.from({ length: blockCount }).map((_, b) => {
          const blockRows = rows.filter((r) => (r.shift_number ?? 1) - 1 === b);
          return (
            <div
              key={b}
              style={{
                display: "flex",
                borderBottom: "1px solid var(--border)",
                background: b % 2 === 0 ? "transparent" : "var(--bg-2)",
              }}
            >
              <div style={{ width: 80, padding: "4px 8px", fontSize: "0.85em" }}>#{b + 1}</div>
              <div style={{ position: "relative", flex: 1, height: 24 }}>
                {blockRows.map((r) => {
                  const m = hhmmToMin(r.start_time);
                  if (m == null) return null;
                  const left = (m - minH * 60) * pxPerMin;
                  const isUp = r.direction.toLowerCase() === "upward";
                  return (
                    <span
                      key={r.row_no}
                      title={`${r.start_time} · ${r.direction} · ${r.trip_duration_min ?? "?"}min`}
                      style={{
                        position: "absolute",
                        left,
                        top: 4,
                        fontSize: "0.85em",
                        fontWeight: 600,
                      }}
                      className={isUp ? "sched-dir-upward" : "sched-dir-downward"}
                    >
                      {isUp ? "▲" : "▼"}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 12, padding: "6px 10px", fontSize: "0.75em", color: "var(--text-3)" }}>
        <span><span className="sched-dir-upward">▲</span> upward</span>
        <span><span className="sched-dir-downward">▼</span> downward</span>
        <span style={{ marginLeft: "auto" }}>
          Edit individual trips from the imports table after saving.
        </span>
      </div>
    </div>
  );
}
